import {ReactNode} from "react";

// Assets
import IconFire from "../../../Assets/SVGs/Icons/Highlight/Fire";
import IconDiamond from "../../../Assets/SVGs/Icons/Highlight/Diamond";
import IconTreasureChest from "../../../Assets/SVGs/Icons/Highlight/TreasureChest";
import ButtonPillGradient from "../../Buttons/ButtonPillGradient";

// Constants
import {RoutePaths} from "../../../Constants/RoutePaths";

// Models
import PlanDTO from "../../../Models/DTOs/Plan";

// Helpers
import {formatMoney} from "../../../Helpers/Currency/Currency";
import {getOfferPercentOff, getOfferRenewalText, getResolvedOffer, hasSpecialOfferStyling, isPrepaidOffer} from "../../../Helpers/BillingOffers";
import {withPromotionCode} from "../../../Helpers/PromotionCode";

const HeroWithPricingPlans = (props: {
    mainMenuDesktopMode: boolean,
    heading: ReactNode,
    tagline?: string,
    backgroundDesktop?: string,
    backgroundMobile?: string,
    planOneTitleBG: string,
    planTwoTitleBG: string,
    planInnerBG: string,
    requireTransition: boolean,
    plans: PlanDTO[] | null,
    plansLoading: boolean,
    plansError: string,
}) => {
    const
        {plans, plansLoading, plansError} = props,

        // Helpers
        periodLabel = (p: number | undefined) =>
            p === 1 ? "Annual Membership" : "Monthly Membership",

        titleBgForIndex = (i: number) =>
            i % 2 === 0 ? props.planOneTitleBG : props.planTwoTitleBG,

        toSlugQuery = (plan: PlanDTO) =>
            encodeURIComponent((plan.title || "").replace(/\s+/g, "-")),

        percentOff = (orig: number | undefined, sale: number | undefined) => {
            if (!orig || !sale || orig <= 0) return undefined;
            return Math.round(((orig - sale) / orig) * 100);
        },

        renderPriceBlock = (plan: PlanDTO) => {
            const offer = getResolvedOffer(plan);

            if (offer) {
                const pct = getOfferPercentOff(offer);
                return (
                    <div className="plan__price">
                        {pct ? <p className="plan__percent-discount">-{pct}%</p> : null}
                        <p className="plan__combined-price">{!isPrepaidOffer(offer) && <span
                            className="plan__strikethrough">{formatMoney(offer.base_amount_minor, offer.currency)}</span>}
                            {formatMoney(offer.final_amount_minor, offer.currency)}
                        </p>

                    </div>
                );
            }

            // 1) Campaign promotion (active) → always show % off
            if (!plan.offerCatalogResolved && plan.promotionIsActive && plan.promotionPrice != null) {
                const pct = percentOff(plan.price, plan.promotionPrice);
                return (
                    <div className="plan__price">
                        {pct ? <p className="plan__percent-discount">-{pct}%</p> : null}
                        <p className="plan__combined-price"><span
                            className="plan__strikethrough">{formatMoney(plan.price, plan.currency)}</span>
                            {formatMoney(plan.promotionPrice, plan.currency)}
                        </p>

                    </div>
                );
            }

            // 2) Standard discount present → show % only for Game Changer
            if (!plan.offerCatalogResolved && plan.standardDiscount?.stdPromoPrice != null) {
                const pct = percentOff(plan.price, plan.standardDiscount.stdPromoPrice);

                return (
                    <div className="plan__price">
                        {pct ? <p className="plan__percent-discount">-{pct}%</p> : null}
                        {formatMoney(plan.standardDiscount.stdPromoPrice, plan.currency)}

                    </div>
                );
            }

            // 3) No discounts
            return (
                <div className="plan__price">
                    {formatMoney(plan.price, plan.currency)}

                </div>
            );
        };

    return (
        <section className="layout layout--hero-pricing-plans u-relative_hidden">
            <div className="hero__inner">
                {props.heading}

                {props.tagline && <p className="hero__tagline">{props.tagline}</p>}

                <ul>
                    <li>
                        <div className="hero__icon-wrapper">{IconFire()}</div>
                        <span>Uncensored, unfiltered weekly news shows</span>
                    </li>
                    <li>
                        <div className="hero__icon-wrapper">{IconDiamond()}</div>
                        {" "}
                        <span>Exclusive original Ickonic documentaries</span>
                    </li>
                    <li>
                        <div className="hero__icon-wrapper">{IconTreasureChest()}</div>
                        {" "}
                        <span>Over 30 years of David Icke content</span>
                    </li>
                </ul>

                <div className="hero__plans">
                    {/* Loading / plansError states (optional) */}
                    {plansLoading && <div className="plan__plansLoading">Loading plans…</div>}
                    {!plansLoading && plansError && (
                        <div className="plan__plansError">Failed to load plans.</div>
                    )}

                    {!plansLoading &&
                        !plansError &&
                        Array.isArray(plans) &&
                        plans.map((plan, i) => {
                            const offer = getResolvedOffer(plan);
                            const ribbonText =
                                offer?.headline ||
                                (!plan.offerCatalogResolved && plan.promotionIsActive && plan.promotionTitle) ||
                                plan.title;

                            const specialOffer = hasSpecialOfferStyling(offer);
                            const saleClass = specialOffer ? ` membership-sale membership-sale--${plan.period === 1 ? "annual" : "monthly"}` : "";

                            const features =
                                Array.isArray(plan.features) && plan.features.length
                                    ? plan.features.slice(0, 3) // show up to 3
                                    : ["Instant Access"];

                            return (
                                <div
                                    key={`${plan.planId}-${plan.key || plan.title}`}
                                    className={`hero__single-plan${saleClass}`}
                                    style={specialOffer ? undefined : {background: props.planInnerBG}}
                                >
                                    <p className="plan__title" style={specialOffer ? undefined : {background: titleBgForIndex(i), color: "white"}}>
                                        {ribbonText}
                                    </p>

                                    <div className="plan__inner">
                                        {specialOffer && <span className="membership-sale__badge">{offer?.badge || "Special offer"}</span>}
                                        <p className="plan__cadence">{periodLabel(plan.period)}</p>

                                        {renderPriceBlock(plan)}

                                        {getOfferRenewalText(offer) && <p className="plan__follow-on">{getOfferRenewalText(offer)}</p>}

                                        {offer?.description && <p>{offer.description}</p>}

                                        <ul>
                                            {features.map((f, idx) => (
                                                <li key={idx}>{f}</li>
                                            ))}
                                        </ul>

                                        <ButtonPillGradient
                                            background={specialOffer ? (plan.period === 1 ? "linear-gradient(110deg, #e7c583, #fff0c9)" : "linear-gradient(110deg, #b79aee, #eadbff)") : "linear-gradient(0deg, rgba(187, 54, 184, 1) 0%, #b760b4 100%)"}
                                            backgroundHover={specialOffer ? (plan.period === 1 ? "#fff0c9" : "#eadbff") : "#b760b4"}
                                            color={specialOffer ? "#20172d" : "white"}
                                            text={offer?.cta_label || "Select Plan"}
                                            link={withPromotionCode(`${RoutePaths.SignUp[0]}?plan=${toSlugQuery(plan)}`)}
                                        />

                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>

            <div
                className="u-full_cover_absolute hero__background"
                style={{
                    background:
                        props.backgroundDesktop && props.backgroundDesktop
                            ? `url(${
                                props.mainMenuDesktopMode
                                    ? props.backgroundDesktop
                                    : props.backgroundMobile
                            }) center bottom/cover no-repeat #000000`
                            : "none",
                }}
            />

            {props.requireTransition && <div className="layout__gradient-transition"/>}
        </section>
    );
};

export default HeroWithPricingPlans;
